import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { MenuKey, NAV_ITEMS, PANEL_WIDTHS, track } from "./navConfig";
import { FOCUS } from "./primitives";
import { LatestMenu } from "./LatestMenu";
import { SectorsMenu } from "./SectorsMenu";
import { MarketsMenu } from "./MarketsMenu";
import { PremiumMenu } from "./PremiumMenu";
import { MoreMenu } from "./MoreMenu";

const PANELS: Record<MenuKey, () => ReactElement> = {
  latest: LatestMenu,
  sectors: SectorsMenu,
  markets: MarketsMenu,
  premium: PremiumMenu,
  more: MoreMenu,
};

export const LayerNav = () => {
  const [open, setOpen] = useState<MenuKey | null>(null);
  const [panelH, setPanelH] = useState<number | null>(null);
  const location = useLocation();
  const navRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const triggerRefs = useRef<Partial<Record<MenuKey, HTMLElement>>>({});
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollStart = useRef(0);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  }, []);
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(null), 170);
  }, [cancelClose]);
  const openMenu = useCallback((key: MenuKey) => {
    cancelClose();
    setOpen((cur) => {
      if (cur !== key) track("nav_menu_open", { menu: key });
      return key;
    });
  }, [cancelClose]);

  useEffect(() => setOpen(null), [location.pathname, location.search]);

  useEffect(() => {
    if (!open) { setPanelH(null); return; }
    const el = contentRef.current;
    if (!el) return;
    const measure = () => setPanelH(el.offsetHeight + 2);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    scrollStart.current = window.scrollY;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(null);
        triggerRefs.current[open]?.focus();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setOpen(null);
    };
    const onScroll = () => {
      if (Math.abs(window.scrollY - scrollStart.current) > 60) setOpen(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onScroll);
    };
  }, [open]);

  const Panel = open ? PANELS[open] : null;

  return (
    <nav ref={navRef} data-testid="mega-nav" aria-label="Primary"
      className="hidden lg:flex items-center justify-center shrink-0 h-full whitespace-nowrap"
      onMouseLeave={scheduleClose} onMouseEnter={cancelClose}>
      {NAV_ITEMS.map((item) => {
        const key = item.panel;
        const active = key !== null && open === key;
        const base = `relative flex items-center gap-1 px-1.5 xl:px-3 h-full text-sm xl:text-[15px] font-semibold tracking-[-0.01em] transition-colors duration-200 rounded ${FOCUS}`;
        const tone = item.key === "premium"
          ? active ? "text-[#A78BFA]" : "text-[#A78BFA] hover:text-white"
          : active ? "text-[#00E5FF]" : "text-gray-400 hover:text-white";
        const indicator = active && (
          <>
            <span aria-hidden className="absolute left-2 right-2 -bottom-px h-[2px] rounded-full bg-gradient-to-r from-[#7C3AED] via-[#8B5CF6] to-[#00E5FF] shadow-[0_0_10px_rgba(124,58,237,0.55)]" />
            <span aria-hidden className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-[calc(100%+6px)] h-2.5 w-2.5 rotate-45 rounded-[2px] bg-[#07080c] border-l border-t border-white/10 z-30" />
          </>
        );
        const chevron = key && (
          <ChevronDown aria-hidden className={`hidden xl:block h-3 w-3 transition-transform duration-200 ${active ? "rotate-180" : ""}`} />
        );
        const hoverProps = key ? { onMouseEnter: () => openMenu(key) } : { onMouseEnter: cancelClose };
        return item.path ? (
          <NavLink key={item.key} to={item.path} data-testid={`nav-link-${item.key}`}
            ref={(el) => { if (key && el) triggerRefs.current[key] = el; }}
            aria-haspopup={key ? "true" : undefined}
            aria-expanded={key ? active : undefined}
            aria-controls={key ? `layernav-panel-${key}` : undefined}
            className={({ isActive }) => `${base} ${isActive && !open ? "text-[#00E5FF]" : tone}`}
            {...hoverProps}>
            {item.name}{chevron}{indicator}
          </NavLink>
        ) : (
          <button key={item.key} type="button" data-testid={`nav-link-${item.key}`}
            ref={(el) => { if (key && el) triggerRefs.current[key] = el; }}
            aria-haspopup="true" aria-expanded={active} aria-controls={`layernav-panel-${key}`}
            onClick={() => (active ? setOpen(null) : openMenu(key as MenuKey))}
            className={`${base} ${tone}`} {...hoverProps}>
            {item.name}{chevron}{indicator}
          </button>
        );
      })}

      {open && Panel && (
        <>
          {createPortal(
            <div aria-hidden
              className="fixed inset-0 z-40 bg-black/20 pointer-events-none motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200" />,
            document.body,
          )}
          <div id={`layernav-panel-${open}`} role="region" aria-label={`${open} menu`} data-testid="mega-panel"
            onMouseEnter={cancelClose}
            className="absolute left-1/2 -translate-x-1/2 top-full mt-2.5 cursor-default whitespace-normal
              rounded-2xl border border-white/10 bg-[#07080c]/[0.995] backdrop-blur-xl
              shadow-[0_30px_90px_-15px_rgba(0,0,0,0.95),0_0_40px_-20px_rgba(124,58,237,0.4)]
              transition-[width,height] duration-200 ease-out overflow-hidden"
            style={{ width: `min(${PANEL_WIDTHS[open]}px, calc(100vw - 96px))`, height: panelH ?? undefined }}>
            <div aria-hidden className="h-[2px] bg-gradient-to-r from-[#7C3AED] via-[#6366F1] to-[#00E5FF] opacity-80" />
            <div key={open} ref={contentRef}
              className="relative motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:duration-200">
              <Panel />
            </div>
          </div>
        </>
      )}
    </nav>
  );
};
